import { Injectable } from '@angular/core';
import { MwLoadOptions } from 'app/models/load-options.model';

const and = '&';

/* eslint-disable @typescript-eslint/quotes */

/* DevExtreme possible action list */
enum FilterOperators {
  '=' = ' eq ',
  '<>' = ' ne ',
  '>' = ' gt ',
  '>=' = ' ge ',
  '<' = ' lt ',
  '<=' = ' le ',
  'contains' = 'substringof',
  'notcontains' = 'not substringof',
  'and' = ' and ',
  'or' = ' or ',
  'startswith' = 'startswith',
  'endswith' = 'endswith',
  '!' = ' not ',
}
/* DevExteme filters array */
enum FilterPartEnum {
  'parameterName' = 0,
  'operatorName' = 1,
  'operationValue' = 2,
}
@Injectable({ providedIn: 'root' })
export class MwLoadOptionsService {
  buildQuery(data: MwLoadOptions): string {
    return [
      this.getPaginationPart(data),
      this.getSortingPart(data),
      this.getFiltersPart(data),
      data.hasCount ? '$inlinecount=allpages' : '',
    ]
      .filter((t) => !!t)
      .join('&');
  }

  private getFiltersPart(data: MwLoadOptions): string {
    const filters = data.filter;
    let filterUrl = '';
    filterUrl = this.nestedFilterLogic(filters, data);
    return filterUrl.length > 0 ? '$filter=' + filterUrl : '';
  }

  private nestedFilterLogic(filters: any, loadOptions: MwLoadOptions): string {
    let partUrl = '';

    /* If Filter array is 3 elements long and first and 
    last elements arn`t arrays, its lowest nest level, that doesn't need to go deeper*/
    if (
      filters.length === 3 &&
      !(filters[FilterPartEnum.parameterName] instanceof Array) &&
      !(filters[FilterPartEnum.operationValue] instanceof Array)
    ) {
      return this.convertFilterRule(filters, loadOptions);
    }
    /* If Filter array is 2 elements long its using 
    negate only that needs to be separated*/
    if (filters.length === 2) {
      return 'not(' + this.nestedFilterLogic(filters[1], loadOptions) + ')';
    }
    /* Normal logic */
    for (const filterRule of filters) {
      if (filterRule[FilterPartEnum.parameterName] instanceof Array) {
        partUrl += '(';
        partUrl += this.nestedFilterLogic(filterRule, loadOptions);
        partUrl += ')';
      } else {
        if (filterRule instanceof Array) {
          partUrl += this.nestedFilterLogic(filterRule, loadOptions);
        } else {
          partUrl += this.convertFilterRule(filterRule, loadOptions);
        }
      }
    }

    return partUrl;
  }

  private convertFilterRule(
    filterRule: any,
    loadOptions: MwLoadOptions
  ): string {
    if (filterRule instanceof Array) {
      return this.convertFilterRuleFunctions(filterRule, loadOptions);
    } else {
      return FilterOperators[filterRule];
    }
  }

  private convertFilterRuleFunctions(
    filterRule: Array<any>,
    options: MwLoadOptions
  ): string {
    switch (FilterOperators[filterRule[FilterPartEnum.operatorName]]) {
      case FilterOperators.contains: {
        return (
          FilterOperators.contains +
          "('" +
          this.escapeUnsuportedOdataCharacters(
            filterRule[FilterPartEnum.operationValue]
          ) +
          "', " +
          this.modifyFieldName(
            filterRule[FilterPartEnum.parameterName],
            options
          ) +
          ')'
        );
      }
      case FilterOperators.notcontains: {
        return (
          FilterOperators.notcontains +
          "('" +
          this.escapeUnsuportedOdataCharacters(
            filterRule[FilterPartEnum.operationValue]
          ) +
          "', " +
          this.modifyFieldName(
            filterRule[FilterPartEnum.parameterName],
            options
          ) +
          ')'
        );
      }
      case FilterOperators.startswith: {
        return (
          FilterOperators.startswith +
          '(' +
          this.modifyFieldName(
            filterRule[FilterPartEnum.parameterName],
            options
          ) +
          ", '" +
          this.escapeUnsuportedOdataCharacters(
            filterRule[FilterPartEnum.operationValue]
          ) +
          "')"
        );
      }
      case FilterOperators.endswith: {
        return (
          FilterOperators.endswith +
          '(' +
          this.modifyFieldName(
            filterRule[FilterPartEnum.parameterName],
            options
          ) +
          ", '" +
          this.escapeUnsuportedOdataCharacters(
            filterRule[FilterPartEnum.operationValue]
          ) +
          "')"
        );
      }
      default: {
        return (
          this.modifyFieldName(
            filterRule[FilterPartEnum.parameterName],
            options
          ) +
          FilterOperators[filterRule[FilterPartEnum.operatorName]] +
          this.modifyParameterValues(filterRule, options)
        );
      }
    }
  }

  /* parameter modifications for String and DateTime to be executed as odata query */
  private modifyParameterValues(
    filterRule: Array<any>,
    loadOptions: MwLoadOptions
  ): string {
    /* Numeric params and empty */
    if (
      loadOptions.numericParameters[filterRule[FilterPartEnum.parameterName]] ||
      filterRule[FilterPartEnum.operationValue] === null
    ) {
      return filterRule[FilterPartEnum.operationValue];
    }
    /* Datetime values*/
    if (loadOptions.dateFields[filterRule[FilterPartEnum.parameterName]]) {
      const date = new Date(filterRule[FilterPartEnum.operationValue]);
      return "datetime'" + date.toISOString().slice(0, -1) + "'";
    }
    /* Defaults */
    return (
      "'" +
      this.escapeUnsuportedOdataCharacters(
        filterRule[FilterPartEnum.operationValue]
      ) +
      "'"
    );
  }

  private getPaginationPart(data: MwLoadOptions): string {
    const pagination =
      data.skip > 0 && data.take > 0
        ? '$skip=' + data.skip + and + '$top=' + data.take
        : data.take > 0
        ? '$top=' + data.take
        : '';
    return pagination;
  }

  private getSortingPart(data: MwLoadOptions): string {
    let orderBy = '';
    if (data.sort.length > 0) {
      orderBy = '$orderby=';
      for (let orderRule = 0; orderRule < data.sort.length; orderRule++) {
        orderBy +=
          this.modifyFieldName(data.sort[orderRule].selector, data) +
          (data.sort[orderRule].desc ? ' desc' : ' asc') +
          (orderRule !== data.sort.length - 1 ? ',' : '');
      }
    }
    return orderBy;
  }

  private modifyFieldName(fieldName: string, options: MwLoadOptions): string {
    if (options.uniqueNamingCaseFields[fieldName]) {
      return options.uniqueNamingCaseFields[fieldName];
    }
    return this.capitalizeFirstLetter(fieldName);
  }

  private capitalizeFirstLetter(word: string): string {
    return word.charAt(0).toUpperCase() + word.slice(1);
  }

  private escapeUnsuportedOdataCharacters(inputText): string {
    // Escaping special symbols
    return (
      encodeURIComponent(inputText)
        // Quotes and Double quotes should be replaced, to avoid crashes
        .replace(/'/g, "''")
        .replace(/"/g, '""')
    );
  }
}
